import { push, RouterAction } from 'react-router-redux';
import { Reducer } from 'redux';
import { AppDispatch } from '../appStore';
import Constants from '../constants';
import ContentConstants from '../contentConstants';
import { ISummaryItem, SurveyStatusType } from '../models';
import * as SurveyAPI from '../services/SurveyAPI';
import * as Actions from './Actions';
import { ActionTypes } from './ActionTypes';

export interface IConfirmScheduleState {
	errorItems?: ISummaryItem[];
	summaryItems?: ISummaryItem[];
	previewItems?: ISummaryItem[];
	summaryItemsLoading: boolean;
	publishing: boolean;
}

export const defaultState: IConfirmScheduleState = {
	errorItems: undefined,
	summaryItems: undefined,
	previewItems: undefined,
	summaryItemsLoading: false,
	publishing: false,
};

export const ActionCreators = {
	Schedule:
		(surveyId: number, redirectToOnSuccess: RouterAction = push(`/${surveyId}${Constants.ROUTE_COMPLETE}`)) =>
		(dispatch: AppDispatch) => {
			dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });
			dispatch({ type: ActionTypes.SURVEY_PUBLISH_REQUESTED });
			return SurveyAPI.publishSurvey(surveyId)
				.then(() => {
					dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
					dispatch({ type: ActionTypes.SURVEY_PUBLISH_SUCCEEDED });
					dispatch(redirectToOnSuccess);
				})
				.catch(() => {
					dispatch({
						type: ActionTypes.TOAST_WARNING_CUSTOM_NOTIFICATION,
						payload: {
							message: ContentConstants.TOAST_ERROR_PUBLISH_FAILURE_BODY,
							messageTitle: ContentConstants.TOAST_ERROR_PUBLISH_FAILURE_TITLE,
						},
					});
					dispatch({ type: ActionTypes.SURVEY_PUBLISH_FAILED });
				});
		},
	MakeTemplatePublic: (surveyId: number) => (dispatch: any) => {
		dispatch({ type: ActionTypes.TOAST_PROCESSING_GENERIC_NOTIFICATION });
		return SurveyAPI.setTemplateStatus(surveyId, SurveyStatusType.PublicSurveyTemplate).then(() => {
			dispatch({ type: ActionTypes.TOAST_DISMISS_ALL });
			dispatch(push(`/`));
		});
	},
	GetSurveySummaryItems:
		(surveyId: number, ignoreValidations = false, includeUnsupportedLanguageWarning = false) =>
		(dispatch: any) => {
			dispatch({ type: ActionTypes.SURVEYSUMMARYITEMS_GET_REQUESTED });

			return SurveyAPI.getSurveySummaryItems(surveyId, { ignoreValidations, includeUnsupportedLanguageWarning })
				.then((result: ISummaryItem[]) => dispatch({ type: ActionTypes.SURVEYSUMMARYITEMS_GET_SUCCEEDED, payload: result }))
				.catch(() => dispatch({ type: ActionTypes.SURVEYSUMMARYITEMS_GET_FAILED }));
		},
};

type KnownActions =
	| Actions.ISurveySummaryItemsGetRequested
	| Actions.ISurveySummaryItemsGetFailed
	| Actions.ISurveySummaryItemsGetSucceeded
	| Actions.ISurveyPublishFailed
	| Actions.ISurveyPublishRequested
	| Actions.ISurveyPublishSucceeded
	| Actions.ISurveyPublishPreviewSucceeded;

export const reducer: Reducer<IConfirmScheduleState, KnownActions> = (
	state: IConfirmScheduleState | undefined,
	action: KnownActions,
): IConfirmScheduleState => {
	if (!state) {
		return defaultState;
	}
	switch (action.type) {
		// SURVEY SUMMARY
		case ActionTypes.SURVEYSUMMARYITEMS_GET_REQUESTED:
			return { ...state, summaryItemsLoading: true };
		case ActionTypes.SURVEYSUMMARYITEMS_GET_FAILED:
			return { ...state, summaryItemsLoading: false };
		case ActionTypes.SURVEYSUMMARYITEMS_GET_SUCCEEDED: {
			const errorItems = action.payload.filter((i) => i.propertyName === 'Errors');
			const previewItems = action.payload.filter((i) => i.propertyName === 'SurveyPreview');
			const nonSummaryItemProperties = ['Errors', 'SurveyPreview'];
			const summaryItems = action.payload.filter((i) => nonSummaryItemProperties.indexOf(i.propertyName) === -1);
			return { ...state, summaryItemsLoading: false, summaryItems, errorItems, previewItems };
		}
		// PUBLISHING
		case ActionTypes.SURVEY_PUBLISH_FAILED:
			return { ...state, publishing: false };
		case ActionTypes.SURVEY_PUBLISH_REQUESTED:
			return { ...state, publishing: true };
		case ActionTypes.SURVEY_PUBLISH_SUCCEEDED:
		case ActionTypes.SURVEY_PUBLISH_PREVIEW_SUCCEEDED:
			return { ...state, publishing: false };
		default:
			return { ...state };
	}
};
